import { bindSnippetCatalogueHints } from '../../features/quick-replies/models/snippetCatalogueHints';
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { useAuth } from "./AuthContext";
import { triggerUnauthorized } from "../axiosSetup";
import { bindSrmPresence } from "./srmPresence";
import { recordMessagingRecovery } from "./messagingRecoveryDiagnostics";
import { registerSessionReset } from '../../utils/sessionLifecycle';
import { bindCatalogueRequestHints } from '../../features/catalogue/models/catalogueRequestHints';
import {
  REFRESH_LOCATION_OPTIONS,
  isDeviceLocationEnabledForUser,
  reportInitialDeviceLocationOnce,
  requestBrowserLocation,
} from "../auth/deviceLocationModel";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { authState } = useAuth();
  const deviceLocationUserType = authState?.user?.user_type;
  const deviceLocationEnabled = isDeviceLocationEnabledForUser({ user_type: deviceLocationUserType });
  const [socket, setSocket] = useState(null);
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return require('../../utils/speedAudit').observeSpeedSocket(socket);
  }, [socket]);
  useEffect(() => {
    const cleanupRequests=bindCatalogueRequestHints(socket),cleanupSnippets=bindSnippetCatalogueHints(socket);
    return () => { cleanupRequests(); cleanupSnippets(); };
  }, [socket]);
  const [isConnected, setIsConnected] = useState(false);
  const [isNetworkOnline, setIsNetworkOnline] = useState(() => navigator.onLine);
  const [srmPresence, setSrmPresence] = useState(null);
  const [watiCreditData, setWatiCreditData] = useState(null);
  const [watiCreditLoading, setWatiCreditLoading] = useState(false);

  const refreshWatiCredit = useCallback(async () => {
    if (!authState?.isAuthenticated) return null;
    setWatiCreditLoading(true);
    try {
      const response = await axios.get(API_DOMAIN + "/wati_messages/get_credits_status", {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      const data = response.data?.success === false ? null : (response.data?.data ?? response.data);
      setWatiCreditData(data);
      return data;
    } catch (_) {
      return null;
    } finally {
      setWatiCreditLoading(false);
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      setWatiCreditData(null);
      return;
    }
    refreshWatiCredit();
  }, [authState?.id, authState?.isAuthenticated, refreshWatiCredit]);

  useEffect(() => {
    // Fire-and-forget by design: browser permission, GPS and this optional
    // upload must never delay authenticated rendering or normal application use.
    reportInitialDeviceLocationOnce({
      isAuthenticated: authState?.isAuthenticated,
      id: authState?.id,
      session_id: authState?.session_id,
      client_device_id: authState?.client_device_id,
      user: { user_type: deviceLocationUserType },
    });
  }, [authState?.client_device_id, authState?.id, authState?.isAuthenticated, authState?.session_id, deviceLocationUserType]);

  useEffect(() => {
    let active = true;
    const handleOffline = () => {
      recordMessagingRecovery("browser_offline", { connected: Boolean(socket?.connected) });
      setIsNetworkOnline(false);
      setIsConnected(false);
      setSrmPresence(null);
    };
    const handleOnline = () => {
      setIsNetworkOnline(true);
      // An offline browser event need not disconnect the underlying transport.
      // Restore React's state even when no new Socket.IO connect event follows.
      setIsConnected(Boolean(socket?.connected));
      recordMessagingRecovery("browser_online", { connected: Boolean(socket?.connected) });
      // Socket.IO normally reconnects itself, but an explicit connect makes
      // browser-reported recovery immediate if it is currently idle.
      if (socket && !socket.connected) socket.connect();
      if (socket?.connected) socket.emit("srm:presence:get", response => {
        if (active && socket.connected && response?.success === true && response.data?.source === "srm"
          && typeof response.data.is_online === "boolean"
          && String(response.data.user_id) === String(authState?.id)) setSrmPresence(response.data);
      });
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      active = false;
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [socket, authState?.id]);

  useEffect(() => {
    console.log('🔍 SocketProvider - Auth state:', {
      hasId: !!authState?.id,
      isAuthenticated: authState?.isAuthenticated
    });

    if (!authState?.id || !authState.isAuthenticated) {
      console.log('🔍 SocketProvider - No auth, cleaning up socket');
      // The preceding authenticated effect owns and disconnects its socket.
      setSocket(null);
      setIsConnected(false);
      setSrmPresence(null);
      return;
    }

    console.log('🚀 SocketProvider - Creating socket connection for user:', authState.id);

    const newSocket = io(API_DOMAIN, {
      // The backend derives the user id from this JWT — it no longer trusts
      // a client-supplied userId. A missing/invalid/expired token rejects
      // the connection with connect_error "not logged in".
      auth: {
        accessToken: authState.token,
      },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 2000,
    });
    const selfPresence = bindSrmPresence(newSocket, authState.id, setSrmPresence);
    const unregisterSession = registerSessionReset(() => {
      selfPresence.dispose(); newSocket.removeAllListeners?.(); newSocket.disconnect();
    });

    let locationRequestListenerRegistered = false;
    const handleDeviceLocationRequest = (_payload, acknowledge) => {
      if (typeof acknowledge !== "function") return;
      if (!deviceLocationEnabled) {
        acknowledge({
          success: false,
          message: "Device location is available only for Purchasers and Delivery Persons.",
        });
        return;
      }
      requestBrowserLocation(REFRESH_LOCATION_OPTIONS)
        .then(data => acknowledge({ success: true, data }))
        .catch(error => acknowledge({
          success: false,
          message: error?.message || "This device could not provide its current location.",
        }));
    };

    newSocket.on("connect", () => {
      recordMessagingRecovery("socket_connected", { connected: true });
      console.log(`✅ Socket Connected: ${newSocket.id}`);
      setIsConnected(true);
      if (deviceLocationEnabled && !locationRequestListenerRegistered) {
        newSocket.on("auth:device_location_requested", handleDeviceLocationRequest);
        locationRequestListenerRegistered = true;
      }
    });

    newSocket.on("disconnect", (reason) => {
      recordMessagingRecovery("socket_disconnected", { connected: false });
      console.log("❌ Socket Disconnected:", reason);
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("🔴 Socket Connection Error:", error);
      setIsConnected(false);
      if (error.message === "not logged in") {
        triggerUnauthorized();
      }
    });

    // A user's token version can be invalidated while this socket is already
    // connected (admin force-logout or password reset). Treat the explicit
    // event as the authoritative logout signal; the server disconnect that
    // follows is only connection cleanup.
    newSocket.on("auth:force_logout", () => {
      triggerUnauthorized();
    });

    const handleCreditStatusUpdated = payload => {
      if (payload?.success && payload.data) setWatiCreditData(payload.data);
    };
    newSocket.on("wati:credit_status_updated", handleCreditStatusUpdated);

    setSocket(newSocket);

    return () => {
      console.log('🔍 SocketProvider - Cleanup');
      unregisterSession();
      selfPresence.dispose();
      setSrmPresence(null);
      newSocket.off("wati:credit_status_updated", handleCreditStatusUpdated);
      if (locationRequestListenerRegistered) {
        newSocket.off("auth:device_location_requested", handleDeviceLocationRequest);
      }
      newSocket.disconnect();
    };
  }, [authState?.id, authState?.isAuthenticated, authState?.token, deviceLocationEnabled]);

  const emitEvent = useCallback((event, data) => {
    if (socket && isConnected) {
      socket.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit event:', event);
    }
  }, [socket, isConnected]);

  const value = {
    socket,
    isConnected,
    isNetworkOnline,
    emitEvent,
    srmPresence,
    watiCreditData,
    watiCreditBalance: watiCreditData?.creditCustomer?.credit ?? null,
    watiCreditLoading,
    refreshWatiCredit,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
